import { Icon } from "@circleco/compass/components/Icon";
import {
  DragHandle,
  SortableList,
} from "@circleco/compass/components/SortableList";
import type { SortableBaseItem } from "@circleco/compass/components/SortableList";
import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { flushSync } from "react-dom";

import { useIsVisibleOnDOM } from "@/react/hooks/utils/useIsVisibleOnDOM";

import { AdjustImages } from "../../AdjustImages";
import { Scroller, scroll } from "../../Shared/Scroller";
import type { UploadedImage } from "../../store";
import { useImagePostModalStore } from "../../store";
import { useHandleFileSelection } from "../../UploadImages/hooks/useHandleFileSelection";
import {
  MAX_IMAGES,
  useMultiImageUpload,
} from "../../UploadImages/hooks/useMultiImageUpload";
import { useRemoveImage } from "../hooks/useRemoveImage";
import { DeleteImageButton } from "./DeleteImageButton";
import { ThumbnailImage } from "./ThumbnailImage";

type SortableUploadedImage = Omit<UploadedImage, "id"> &
  SortableBaseItem & {
    originalId?: UploadedImage["id"];
  };

interface ThumbnailsProps {
  currentImageIndex: number;
  setCurrentImageIndex: Dispatch<SetStateAction<number>>;
}

export const Thumbnails = ({
  setCurrentImageIndex,
  currentImageIndex,
}: ThumbnailsProps) => {
  const [isAdjustImagesOpen, setIsAdjustImagesOpen] = useState(false);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [leftHookRef, isLeftEndVisible] = useIsVisibleOnDOM<HTMLDivElement>();
  const [rightHookRef, isRightEndVisible] = useIsVisibleOnDOM<HTMLDivElement>();
  const {
    uploadedImages,
    setUploadedImages,
    selectedFiles,
    resetSelectedFiles,
    setIsImagesDirty,
  } = useImagePostModalStore();
  const { handleFileSelection } = useHandleFileSelection(() =>
    setIsAdjustImagesOpen(true),
  );
  const { getInputProps, open } = useMultiImageUpload({
    handleFileSelection,
    selectedFiles,
    uploadedFilesCount: uploadedImages.length,
  });
  const { removeImage } = useRemoveImage();

  const dndImages: SortableUploadedImage[] = uploadedImages.map(image => ({
    ...image,
    id: image.signed_id,
    originalId: image.id,
  }));
  const currentImage = dndImages[currentImageIndex];

  const handleItemsChange = (images: SortableUploadedImage[]) => {
    const newIndex = images.findIndex(
      image => image.signed_id === currentImage?.signed_id,
    );

    if (newIndex >= 0 && newIndex !== currentImageIndex) {
      flushSync(() => {
        setCurrentImageIndex(newIndex);
      });
    }
    setUploadedImages(
      images.map(({ originalId, ...image }) => ({
        ...image,
        id: originalId,
      })),
    );
    setIsImagesDirty(true);
  };

  return (
    <div className="relative mt-6 flex w-full min-w-0 shrink-0 overflow-hidden px-1">
      {!isLeftEndVisible && (
        <Scroller
          direction="left"
          scrollableContainerRef={scrollableContainerRef}
        />
      )}
      <div
        className="scrollbar-hide m-auto flex max-w-full items-center overflow-x-auto px-1 pt-1"
        ref={scrollableContainerRef}
      >
        <div className="size-px" ref={leftHookRef} />
        <SortableList<SortableUploadedImage>
          axis="horizontal"
          className="flex shrink-0 items-center"
          draggingClassName="opacity-60"
          items={dndImages}
          mode="flat"
          onItemsChange={handleItemsChange}
          restrictToContainer
          renderItem={image => {
            const index = uploadedImages.findIndex(
              uploadedImage => uploadedImage.signed_id === image.signed_id,
            );
            const uploadedImage = uploadedImages[index];

            if (!uploadedImage) {
              return <div />;
            }

            return (
              <div
                className="group relative shrink-0"
                data-testid="thumbnail-item"
              >
                <DeleteImageButton
                  onClick={() => removeImage(uploadedImage)}
                  index={index}
                  currentImageIndex={currentImageIndex}
                />
                <DragHandle
                  render={({ ref, style }) => (
                    <div
                      ref={ref}
                      style={style}
                      className="cursor-grab active:cursor-grabbing"
                    >
                      <ThumbnailImage
                        image={uploadedImage}
                        index={index}
                        currentImageIndex={currentImageIndex}
                        onClick={setCurrentImageIndex}
                      />
                    </div>
                  )}
                />
              </div>
            );
          }}
        />
        {uploadedImages.length < MAX_IMAGES && (
          <>
            <button
              type="button"
              className="hover:bg-secondary text-default ml-1 flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-md border border-dashed"
              onClick={() => {
                resetSelectedFiles();
                open();
              }}
            >
              <input {...getInputProps()} />
              <Icon name="plus" size="sm" />
            </button>
            {isAdjustImagesOpen && (
              <AdjustImages
                onClose={() => setIsAdjustImagesOpen(false)}
                onCompleted={() =>
                  setTimeout(
                    () =>
                      scroll({
                        direction: "right",
                        scrollableContainerRef,
                      }),
                    200,
                  )
                }
                isOnTop
              />
            )}
          </>
        )}
        <div className="size-px" ref={rightHookRef} />
      </div>
      {!isRightEndVisible && (
        <Scroller
          direction="right"
          scrollableContainerRef={scrollableContainerRef}
        />
      )}
    </div>
  );
};
